import { pxToRem } from '@modernhealth/vital-components';
import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Colors, colors } from '@modernhealth/vital-foundation';
import { layout } from 'src/shared/styles/variables';

const StyledWrapper = styled('div')({
    position: 'relative',
    justifyContent: 'center',
    display: 'flex',
    flex: 1,
    paddingBottom: pxToRem(88),
});

const StyledContainer = styled('div')({
    maxWidth: layout.containerWidth,
    display: 'flex',
    flex: 1,
});

const StyledBackground = styled('div')((props: { backgroundColor: string }) => ({
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: -1,
    width: '50vw',
    minWidth: '100%',
    backgroundColor: props.backgroundColor,
}));

const StyledColumn = styled('div')((props: { flex: number }) => ({
    display: 'flex',
    flex: props.flex,
    position: 'relative',
    flexDirection: 'column',
}));

const ContentColumn = styled(StyledColumn)`
    padding-left: ${pxToRem(30)};
`;

const SidebarColumn = styled(StyledColumn)`
    border-right: 2px solid ${colors.gray05};
    padding-right: ${pxToRem(30)};
`;

const PageTitleContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const PageTitleInner = styled.div`
    width: ${layout.containerWidth}px;
    max-width: ${layout.containerWidth}px;
`;

const Sidebar: React.FC<{ backgroundColor?: Colors | 'transparent' }> = () => null;
const Content: React.FC = () => null;

function throwErrorInDev(message: string) {
    if (process.env.NODE_ENV === 'production') {
        return;
    }

    throw new Error(message);
}

export enum SidebarLayoutError {
    INCORRECT_NUMBER_OF_CHILDREN = 'The SidebarLayout must only contain up to 1 Sidebar component and exactly 1 Content component',
    INCORRECT_TYPE_OF_CHILDREN = 'Children of the SidebarLayout must only be of type Sidebar or Content',
}

export interface ContainerProps {
    pageTitle?: ReactNode;
}

const Container: React.FC<ContainerProps> = ({ children, pageTitle }) => {
    const { sidebar, content } = React.Children.toArray(children).reduce<{
        sidebar: React.ReactNode;
        content: React.ReactNode;
    }>(
        (accum, child) => {
            if (!React.isValidElement(child)) {
                throwErrorInDev(SidebarLayoutError.INCORRECT_TYPE_OF_CHILDREN);
                return accum;
            }

            if (child.type === Sidebar) {
                if (accum.sidebar) {
                    throwErrorInDev(SidebarLayoutError.INCORRECT_NUMBER_OF_CHILDREN);
                }

                accum.sidebar = (
                    <SidebarColumn flex={1}>
                        {child.props.children}
                        <StyledBackground
                            backgroundColor={
                                child.props.backgroundColor
                                    ? colors[child.props.backgroundColor]
                                    : colors.gray05
                            }
                        />
                    </SidebarColumn>
                );
                return accum;
            }

            if (child.type === Content) {
                if (accum.content) {
                    throwErrorInDev(SidebarLayoutError.INCORRECT_NUMBER_OF_CHILDREN);
                }

                accum.content = <ContentColumn flex={2}>{child.props.children}</ContentColumn>;
                return accum;
            }

            throwErrorInDev(SidebarLayoutError.INCORRECT_TYPE_OF_CHILDREN);
            return accum;
        },
        { sidebar: null, content: null },
    );

    if (!content) {
        throwErrorInDev(SidebarLayoutError.INCORRECT_NUMBER_OF_CHILDREN);
    }

    return (
        <>
            {!!pageTitle && (
                <PageTitleContainer>
                    <PageTitleInner>{pageTitle}</PageTitleInner>
                </PageTitleContainer>
            )}
            <StyledWrapper>
                <StyledContainer>
                    {sidebar}
                    {content}
                </StyledContainer>
            </StyledWrapper>
        </>
    );
};

export const SidebarLayout = {
    Container,
    Sidebar,
    Content,
};
